import { useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Flex,
  Group,
  rem,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconPlugConnectedX,
} from "@tabler/icons-react";
import Button from "@/design-system/components/Button";
import Loading from "@/shared/components/Loading";
import { RoutePaths } from "@/core/constants/RoutePaths";
import { useUserSector } from "@/core/hooks/useUserSector";
import { useSectorWhatsAppHealth } from "@/features/whatsapp-status/hooks/useSectorWhatsAppHealth";
import { deriveCampaignHealthGate } from "@/features/campaigns/utils/campaignHealth";
import { useCampaignDispatchGate } from "@/features/campaigns/hooks/useCampaignDispatchGate";
import { useCampaignBuilderStore } from "@/features/campaigns/stores/campaignBuilderStore";
import { useResetOnSectorChange } from "@/shared/hooks/useResetOnSectorChange";
import { ASK_ADMIN_TO_RESOLVE } from "@/features/whatsapp-status/copy";
import classes from "./CampaignHealthGate.module.css";

/**
 * Gate de prontidão de disparo na ENTRADA do builder/revisão (SA-1170/1196):
 * setor não-oficial (domestic), WhatsApp desconectado, pendência de pagamento
 * (131042/141006) ou envio BLOCKED pela Meta impedem montar o disparo — melhor
 * que deixar o cliente configurar tudo e ver todos os envios falharem. O CTA
 * "Resolver conexão" leva à tela de conexão com o setor na URL (atalho
 * parametrizado — o link funciona encaminhado para o admin que resolve).
 *
 * Fail-open: sem snapshot (endpoint falhou) em setor cloud o builder abre
 * normalmente — o gate é UX preventiva, o erro real da Meta continua aparecendo
 * no envio. A query é a MESMA do banner global do MainLayout (cache warm):
 * conexão saudável entra sem fricção nova. A regra de bloqueio vive em
 * `useCampaignDispatchGate` (compartilhada com a lista).
 *
 * Também é aqui que a troca de setor abandona o fluxo (SA-1277): as QUATRO rotas
 * de builder/revisão (criação e edição) passam por este gate, e o rascunho vive
 * num store que sobrevive à navegação. Sem isto, trocar de conta com o builder
 * aberto deixava na tela o rascunho da conta anterior — o gate não remonta quando
 * a saúde do novo setor já está em cache, e o `prefilledForRef` do builder ignora
 * o erro do refetch, então o usuário seguia editando até o PUT falhar com 403.
 */
const CampaignHealthGate: React.FC = () => {
  const navigate = useNavigate();
  const { activeSectorId } = useUserSector();
  const gate = useCampaignDispatchGate();
  const resetBuilder = useCampaignBuilderStore((state) => state.reset);

  const leaveBuilderOnSectorChange = useCallback(() => {
    resetBuilder();
    navigate(RoutePaths.CAMPAIGNS, { replace: true });
  }, [navigate, resetBuilder]);
  useResetOnSectorChange(leaveBuilderOnSectorChange);

  if (gate.isLoading) {
    return <Loading />;
  }

  if (!gate.blocked || !gate.copy) {
    return <Outlet />;
  }

  return (
    <Flex
      align="center"
      justify="center"
      className={classes.wrapper}>
      <Box className={classes.card}>
        <Stack
          gap={rem(14)}
          align="center">
          <ThemeIcon
            size={rem(56)}
            radius="xl"
            variant="light"
            color="V500">
            <IconPlugConnectedX size={28} />
          </ThemeIcon>

          <Badge
            size="sm"
            radius="sm"
            variant="light"
            color="V500">
            Conexão WhatsApp
          </Badge>

          <Box>
            <Text
              ff="heading"
              fw={700}
              fz={rem(20)}
              c="var(--mantine-color-plain_text-0)">
              {gate.copy.title}
            </Text>
            <Text
              fz={rem(13.5)}
              c="dimmed"
              mt={rem(6)}
              lh={1.6}>
              {gate.copy.description} Para evitar um disparo inteiro falhando,
              resolva a pendência antes de configurar o envio.
              {!gate.canResolveConnection && ` ${ASK_ADMIN_TO_RESOLVE}`}
            </Text>
          </Box>

          <Group
            gap={rem(10)}
            mt={rem(4)}>
            <Button
              variant="ghost"
              radius="md"
              onClick={() => navigate(RoutePaths.CAMPAIGNS)}>
              Voltar
            </Button>
            {/* Sem ui:settings:view a tela de conexão é bloqueada
                (SA-1360): o CTA levaria direto ao bloqueio. */}
            {gate.canResolveConnection && (
              <Button
                radius="md"
                rightSection={<IconArrowRight size={16} />}
                onClick={() =>
                  navigate(
                    `${RoutePaths.SETTINGS_WHATSAPP}?sector=${activeSectorId}`
                  )
                }>
                Resolver conexão
              </Button>
            )}
          </Group>
        </Stack>
      </Box>
    </Flex>
  );
};

export default CampaignHealthGate;

/**
 * Aviso NÃO-bloqueante de qualidade RED no builder (SA-1196): a Meta não
 * recusa envios por qualidade baixa, mas um disparo em massa piora a
 * avaliação do número e aumenta o risco de suspensão. Renderiza null fora
 * do caso RED — mesma query (cacheada) do gate.
 */
export function CampaignQualityRedWarning() {
  const { activeSectorId } = useUserSector();
  const health = useSectorWhatsAppHealth(activeSectorId);
  const gate = deriveCampaignHealthGate(health.data);

  if (!gate.qualityRed) return null;

  return (
    <Alert
      variant="light"
      color="yellow"
      radius="md"
      icon={<IconAlertTriangle size={18} />}
      title="Qualidade do número: baixa">
      Disparar em massa com a qualidade baixa piora a avaliação do número e
      aumenta o risco de suspensão pela Meta. Considere aguardar a reputação se
      recuperar antes de enviar para uma audiência grande.
    </Alert>
  );
}
